import React, { useState, useEffect } from 'react';
import { BarChart3 } from 'lucide-react';

import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Settings, Save, Loader2, RotateCcw, SlidersHorizontal, Database, Calculator, Landmark, Copy, Trash2, Plus, RefreshCw, Home, Info, Lock, Unlock, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import { PriceBaseSection } from './PriceBasePage';
import { CreditoScoringSettingsSection } from '../components/CreditoScoringSettings';
import { TaxRulesSettingsSection } from '../components/TaxRulesSettings';
import { useAuth } from '../contexts/AuthContext';
import ServicersPage from './ServicersPage';
import { AcquisitionExpenses } from '../components/AcquisitionExpenses';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Format the FastAPI/axios error so toasts never display "[object Object]".
// FastAPI 422 returns `detail` as an array of validation errors;
// we collapse it to a readable single-line message.
const errMsg = (err) => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(e => e?.msg || JSON.stringify(e)).join(' · ');
  if (d && typeof d === 'object') return d.msg || JSON.stringify(d);
  return err?.message || 'Error desconocido';
};

const LABELS = {
  estado_inmueble: {
    title: 'Estado del inmueble',
    options: { reformado: 'Reformado', buen_estado: 'Buen estado', habitable: 'Habitable', a_reformar: 'A reformar', reforma_integral: 'Reforma integral' },
  },
  ascensor: {
    title: 'Ascensor',
    options: { si: 'Si', no: 'No' },
  },
  planta: {
    title: 'Planta',
    options: { bajo: 'Bajo', primero: 'Primero', segundo: 'Segundo', tercero: 'Tercero', cuarto: 'Cuarto', atico: 'Atico' },
  },
  altura_sin_ascensor: {
    title: 'Penalizacion altura sin ascensor',
    options: { bajo: 'Bajo', primero: 'Primero', segundo: 'Segundo', tercero: 'Tercero', cuarto: 'Cuarto', atico: 'Atico' },
  },
  exterior_luminosidad: {
    title: 'Exterior / Luminosidad',
    options: { exterior_luminoso: 'Exterior luminoso', exterior_normal: 'Exterior normal', interior: 'Interior', muy_oscuro: 'Muy oscuro' },
  },
  orientacion: {
    title: 'Orientacion',
    options: { sur: 'Sur', este: 'Este', oeste: 'Oeste', norte: 'Norte', doble_orientacion: 'Doble orientacion', no_consta: 'No consta' },
  },
  posesion_val: {
    title: 'Posesion',
    options: { con_posesion: 'Con posesion', vacio: 'Vacio', sin_posesion: 'Sin posesion', no_consta: 'No consta' },
  },
  vistas_ubicacion_micro: {
    title: 'Vistas / Ubicacion',
    options: { vistas_buenas: 'Buenas vistas', zona_normal: 'Zona normal', zona_regular: 'Zona regular', zona_mala: 'Zona mala' },
  },
  distribucion: {
    title: 'Distribucion',
    options: { distribucion_muy_buena: 'Muy buena', distribucion_correcta: 'Correcta', distribucion_mala: 'Mala' },
  },
  extras: {
    title: 'Extras',
    options: { tiene_terraza: 'Terraza', tiene_balcon: 'Balcon', tiene_jardin: 'Jardin', tiene_piscina: 'Piscina', tiene_garaje: 'Garaje', tiene_trastero: 'Trastero' },
  },
};

const SECTION_ORDER = ['estado_inmueble', 'ascensor', 'planta', 'altura_sin_ascensor', 'exterior_luminosidad', 'orientacion', 'posesion_val', 'vistas_ubicacion_micro', 'distribucion', 'extras'];

const SettingsPage = () => {
  const { user } = useAuth();
  const fullAccess = user?.role === 'superadmin';
  const [params, setParams] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  // Read ?tab=precios from URL
  const urlTab = new URLSearchParams(location.search).get('tab');
  const [selectedTab, setActiveTab] = useState(() => {
    if (!fullAccess || urlTab === 'servicer') return 'servicer';
    if (urlTab === 'precios') return 'precios';
    if (urlTab === 'credito') return 'credito';
    if (urlTab === 'impuestos') return 'impuestos';
    if (urlTab === 'adquisicion') return 'adquisicion';
    if (urlTab === 'alquiler') return 'alquiler';
    if (urlTab === 'mercado') return 'mercado';
    if (urlTab === 'datos') return 'datos';
    if (urlTab === 'revisiones') return 'revisiones';
    return 'valoracion';
  });
  // El rol global limita las vistas antes de montar componentes o cargar sus datos.
  const activeTab = fullAccess ? selectedTab : 'servicer';

  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (!fullAccess || tab === 'servicer') setActiveTab('servicer');
    else if (tab === 'precios') setActiveTab('precios');
    else if (tab === 'credito') setActiveTab('credito');
    else if (tab === 'impuestos') setActiveTab('impuestos');
    else if (tab === 'adquisicion') setActiveTab('adquisicion');
    else if (tab === 'alquiler') setActiveTab('alquiler');
    else if (tab === 'mercado') setActiveTab('mercado');
    else if (tab === 'datos') setActiveTab('datos');
    else if (tab === 'revisiones') setActiveTab('revisiones');
    else setActiveTab('valoracion');
  }, [location.search, fullAccess]);

  const switchTab = (t) => {
    if (!fullAccess && t !== 'servicer') return;
    setActiveTab(t);
    const path = t === 'valoracion' ? '/parametros' : `/parametros?tab=${t}`;
    navigate(path, { replace: true });
  };

  useEffect(() => {
    if (activeTab !== 'valoracion') return;
    if (params) return; // Already loaded
    axios.get(`${API}/settings/valuation-params`, { withCredentials: true })
      .then(res => setParams(res.data))
      .catch(() => toast.error('Error al cargar parametros'))
      .finally(() => setLoading(false));
  }, [activeTab, params]);

  const handleChange = (section, key, value) => {
    setParams(prev => ({
      ...prev,
      [section]: { ...prev[section], [key]: parseFloat(value) || 0 },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/valuation-params`, params, { withCredentials: true });
      toast.success('Parametros guardados');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!window.confirm('Restaurar valores por defecto?')) return;
    setLoading(true);
    try {
      // Delete current settings to fall back to defaults
      const res = await axios.get(`${API}/settings/valuation-params`, { withCredentials: true });
      // We need to send defaults — fetch them by deleting and re-getting
      // Actually just reload defaults from a known state
      const defaults = {
        estado_inmueble: { reformado: 15, buen_estado: 5, habitable: 0, a_reformar: -10, reforma_integral: -20 },
        ascensor: { si: 3, no: 0 },
        planta: { bajo: -5, primero: 0, segundo: 1, tercero: 2, cuarto: 3, atico: 8 },
        altura_sin_ascensor: { bajo: -3, primero: -2, segundo: -5, tercero: -10, cuarto: -15, atico: -15 },
        exterior_luminosidad: { exterior_luminoso: 5, exterior_normal: 2, interior: -5, muy_oscuro: -8 },
        orientacion: { sur: 4, este: 2, oeste: 1, norte: -3, doble_orientacion: 5, no_consta: 0 },
        posesion_val: { con_posesion: 0, vacio: 2, sin_posesion: -20, no_consta: -10 },
        vistas_ubicacion_micro: { vistas_buenas: 5, zona_normal: 0, zona_regular: -5, zona_mala: -10 },
        distribucion: { distribucion_muy_buena: 4, distribucion_correcta: 0, distribucion_mala: -5 },
        extras: { tiene_terraza: 5, tiene_balcon: 2, tiene_jardin: 8, tiene_piscina: 7, tiene_garaje: 5, tiene_trastero: 2 },
      };
      await axios.put(`${API}/settings/valuation-params`, defaults, { withCredentials: true });
      setParams(defaults);
      toast.success('Valores por defecto restaurados');
    } catch { toast.error('Error al restaurar'); }
    finally { setLoading(false); }
  };

  if (activeTab === 'valoracion' && loading) return (
    <div className="space-y-6" data-testid="settings-page">
      <SettingsHeader activeTab={activeTab} switchTab={switchTab} fullAccess={fullAccess} />
      <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="settings-page">
      <SettingsHeader activeTab={activeTab} switchTab={switchTab} fullAccess={fullAccess} />

      {activeTab === 'precios' && <PriceBaseSection />}
      {activeTab === 'credito' && <CreditoScoringSettingsSection />}
      {activeTab === 'impuestos' && <TaxRulesSettingsSection />}
      {activeTab === 'adquisicion' && <AcquisitionExpenses />}
      {activeTab === 'alquiler' && <RentalDefaultsSection />}
      {activeTab === 'mercado' && <MarketPricesSection />}
      {activeTab === 'datos' && <><Link to="/parametros?tab=servicer" className="btn-primary inline-flex mb-4" data-testid="settings-servicer-assignment">Limpieza de datos · Asignación de SERVICER</Link><DataHygieneSection /></>}
      {activeTab === 'servicer' && <ServicersPage embedded />}
      {activeTab === 'revisiones' && <ReviewDeadlinesSection />}
      {/* Nueva subpestaña VENTA (reemplaza la antigua "Valoración" en el UI).
          Los parámetros subjetivos legacy (luminosidad, orientación, vistas,
          distribución, extras) permanecen en `db.settings.valuation_params`
          pero DEJAN de mostrarse y NO se aplican en cálculos nuevos. */}
      {activeTab === 'valoracion' && <SaleAdjustmentsSection />}
    </div>
  );
};

const SettingsHeader = ({ activeTab, switchTab, fullAccess }) => (
  <div className="space-y-4">
    <div>
      <h1 className="heading-1 flex items-center gap-2"><Settings className="w-6 h-6 text-blue-600" /> Parámetros</h1>
      <p className="body-text mt-1">{fullAccess ? 'Ajustes de valoración, base de precios m² y SERVICERS' : 'Gestión de SERVICERS'}</p>
    </div>
    <div className="flex flex-wrap gap-1 border-b border-slate-200" role="tablist" aria-label="Secciones de Parámetros" data-testid="settings-tabs">
      {fullAccess && <>
      <TabButton active={activeTab === 'adquisicion'} onClick={() => switchTab('adquisicion')} icon={<Calculator className="w-4 h-4" />} label="Adquisición" testId="tab-adquisicion" />
      <TabButton
        active={activeTab === 'valoracion'}
        onClick={() => switchTab('valoracion')}
        icon={<SlidersHorizontal className="w-4 h-4" />}
        label="VENTA"
        testId="tab-valoracion"
      />
      <TabButton
        active={activeTab === 'precios'}
        onClick={() => switchTab('precios')}
        icon={<Database className="w-4 h-4" />}
        label="IDEALISTA"
        testId="tab-precios"
      />
      <TabButton
        active={activeTab === 'mercado'}
        onClick={() => switchTab('mercado')}
        icon={<Database className="w-4 h-4" />}
        label="FOTOCASA"
        testId="tab-mercado"
      />
      <TabButton
        active={activeTab === 'credito'}
        onClick={() => switchTab('credito')}
        icon={<Calculator className="w-4 h-4" />}
        label="Scoring Crédito"
        testId="tab-credito"
      />
      <TabButton
        active={activeTab === 'impuestos'}
        onClick={() => switchTab('impuestos')}
        icon={<Landmark className="w-4 h-4" />}
        label="Impuestos"
        testId="tab-impuestos"
      />
      <TabButton
        active={activeTab === 'alquiler'}
        onClick={() => switchTab('alquiler')}
        icon={<Home className="w-4 h-4" />}
        label="ALQUILER"
        testId="tab-alquiler"
      />
      <TabButton
        active={activeTab === 'datos'}
        onClick={() => switchTab('datos')}
        icon={<Copy className="w-4 h-4" />}
        label="Datos"
        testId="tab-datos"
      />
      <TabButton
        active={activeTab === 'revisiones'}
        onClick={() => switchTab('revisiones')}
        icon={<ClipboardList className="w-4 h-4" />}
        label="Revisiones"
        testId="tab-revisiones"
      />
      </>}
      <TabButton
        active={activeTab === 'servicer'}
        onClick={() => switchTab('servicer')}
        icon={<Landmark className="w-4 h-4" />}
        label="SERVICER"
        testId="tab-servicer"
      />
    </div>
  </div>
);

const TabButton = ({ active, onClick, icon, label, testId }) => (
  <button
    type="button"
    role="tab"
    aria-selected={active}
    onClick={onClick}
    data-testid={testId}
    className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
      active
        ? 'border-blue-600 text-blue-600'
        : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
    }`}
  >
    {icon} {label}
  </button>
);

export default SettingsPage;

// ---- Alquiler: parámetros por defecto (Fase 1) ----
// Estos valores se aplican a cualquier activo cuando no exista un override
// específico dentro de `rental_analysis`. Ampliable en fases posteriores.
const RentalDefaultsSection = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [safety, setSafety] = useState('95');
  const [safetyOriginal, setSafetyOriginal] = useState('95');

  useEffect(() => {
    axios.get(`${API}/settings/rental`, { withCredentials: true })
      .then((res) => {
        const s = res.data?.rental_safety_coefficient;
        const sStr = s != null ? String(s) : '95';
        setSafety(sStr);
        setSafetyOriginal(sStr);
      })
      .catch((err) => toast.error(errMsg(err)))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    const safetyNum = Number(safety);
    if (safety === '' || !Number.isFinite(safetyNum) || safetyNum < 0 || safetyNum > 100) {
      toast.error('El coeficiente de seguridad debe estar entre 0 y 100');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/settings/rental`,
        { rental_safety_coefficient: safetyNum },
        { withCredentials: true },
      );
      const s = res.data?.rental_safety_coefficient;
      setSafety(s != null ? String(s) : String(safetyNum));
      setSafetyOriginal(s != null ? String(s) : String(safetyNum));
      toast.success('Parámetros de alquiler guardados');
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('¿Restaurar el coeficiente de seguridad al 95%?')) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings/rental`, { rental_safety_coefficient: 95 }, { withCredentials: true });
      const s = res.data?.rental_safety_coefficient;
      setSafety(s != null ? String(s) : '95');
      setSafetyOriginal(s != null ? String(s) : '95');
      toast.success('Valores por defecto restaurados');
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  const dirty = safety !== safetyOriginal;

  return (
    <div className="space-y-8" data-testid="settings-rental-section">
      {/* Ponderaciones de ALQUILER (sale_adjustments equivalente para alquiler) */}
      <RentalAdjustmentsSection />

      {/* Bloque legacy: gastos de adquisición y defaults por activo */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="heading-2 flex items-center gap-2"><Home className="w-5 h-5 text-blue-600" /> Otros parámetros de Alquiler</h2>
          <p className="body-text mt-1">Valores de alquiler aplicados por defecto.</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleReset}
            disabled={saving}
            className="btn-secondary text-sm flex items-center gap-2 disabled:opacity-60"
            data-testid="rental-settings-reset"
          >
            <RotateCcw className="w-4 h-4" /> Restaurar defecto
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !dirty}
            className="btn-primary flex items-center gap-2 disabled:opacity-60"
            data-testid="rental-settings-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </div>
      </div>

      <Link to="/parametros?tab=adquisicion" className="text-sm text-blue-600 hover:underline inline-flex" data-testid="rental-acquisition-settings-link">Gastos de adquisición estimados →</Link>

      <div className="card-base p-5 max-w-2xl">
        <h3 className="text-sm font-semibold uppercase tracking-wide mb-4" style={{ color: 'var(--color-text-primary)' }}>
          Coeficiente de Seguridad (Prudencia sobre alquiler de mercado)
        </h3>
        <p className="text-xs mb-4" style={{ color: 'var(--color-text-muted)' }}>
          Factor conservador aplicado al precio de alquiler de mercado (Fotocasa) antes de proyectar el ROI. Por defecto 95 % — recoge morosidad, voids y comisiones. Un valor menor implica una proyección más prudente.
          <br />
          <span className="text-slate-400">Ejemplo: precio mercado 1.000 €/mes × 95 % = 950 €/mes aplicado al ROI.</span>
        </p>
        <div className="flex items-center gap-3">
          <label className="text-sm flex-1" style={{ color: 'var(--color-text-primary)' }}>
            Coeficiente global
          </label>
          <input
            type="number"
            value={safety}
            onChange={(e) => setSafety(e.target.value)}
            min={0}
            max={100}
            step="1"
            className="input-base w-24 text-sm text-right tabular-nums"
            data-testid="rental-settings-safety-coef-input"
          />
          <span className="text-sm w-4" style={{ color: 'var(--color-text-muted)' }}>%</span>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Configuraciones de ponderaciones por módulo (sale_adjustments / rental_adjustments)
// Estructura común "en 4 bloques" (Planta+Ascensor · Piscina/Garaje · Estado · Límites)
// que se aplican sobre las property_features del activo (no sobre datos subjetivos).
// ---------------------------------------------------------------------------
const FLOOR_ROWS = [
  { key: 'ground_floor', label: 'Planta baja' },
  { key: 'first_floor', label: '1.º' },
  { key: 'second_with_elevator', label: '2.º con ascensor' },
  { key: 'third_with_elevator', label: '3.º con ascensor' },
  { key: 'fourth_with_elevator', label: '4.º con ascensor' },
  { key: 'fifth_or_higher_with_elevator', label: '5.º o superior con ascensor' },
  { key: 'second_without_elevator', label: '2.º sin ascensor' },
  { key: 'third_without_elevator', label: '3.º sin ascensor' },
  { key: 'fourth_without_elevator', label: '4.º sin ascensor' },
  { key: 'fifth_or_higher_without_elevator', label: '5.º o superior sin ascensor' },
];
const CONDITION_ROWS = [
  { key: 'ready', label: 'Buen estado / listo para usar' },
  { key: 'light_renovation', label: 'Reforma ligera' },
  { key: 'full_renovation', label: 'Reforma integral' },
];

// Fila editable numérica (%). Guardado por-campo optimista con debounce
// no se implementa aquí: guardamos todo con el botón "Guardar cambios"
// para respetar el sistema de guardado actual y "no guardar si no ha cambiado".
const AdjRow = ({ label, value, onChange, testid, help }) => (
  <div className="flex items-center justify-between gap-3 py-2">
    <div className="flex-1 min-w-0">
      <p className="text-sm text-slate-600">{label}</p>
      {help && <p className="text-xs text-slate-400">{help}</p>}
    </div>
    <div className="flex items-center gap-1 shrink-0">
      <input
        type="number"
        value={value ?? 0}
        onChange={(e) => onChange(e.target.value)}
        step="0.5"
        className="w-20 px-2 py-1.5 border border-slate-200 rounded-lg text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
        data-testid={testid}
      />
      <span className="text-xs text-slate-400 w-4">%</span>
    </div>
  </div>
);

const AdjBlock = ({ title, children, testid }) => (
  <div className="card-base p-5" data-testid={testid}>
    <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">{title}</h3>
    <div className="space-y-1">{children}</div>
  </div>
);

// Componente principal reutilizable: renderiza los 4 bloques comunes y guarda
// vía el endpoint indicado. Si `variant === 'rental'`, añade el bloque del
// coeficiente de prudencia.
const AdjustmentsSection = ({ title, subtitle, endpoint, testidPrefix, variant = 'sale' }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [params, setParams] = useState(null);
  const [original, setOriginal] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings/${endpoint}`, { withCredentials: true })
      .then((res) => { setParams(res.data); setOriginal(res.data); })
      .catch((err) => toast.error(errMsg(err)))
      .finally(() => setLoading(false));
  }, [endpoint]);

  const setField = (k, v) => {
    const num = v === '' ? 0 : Number(v);
    setParams((prev) => ({ ...prev, [k]: Number.isNaN(num) ? 0 : num }));
  };

  const dirty = params && original && JSON.stringify(params) !== JSON.stringify(original);

  const handleSave = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      // Enviamos sólo los campos con cambios detectados (mismos que original en
      // el resto → no forzamos escritura innecesaria y validación es idéntica).
      const changed = {};
      for (const k of Object.keys(params)) {
        if (params[k] !== original[k]) changed[k] = params[k];
      }
      const res = await axios.put(`${API}/settings/${endpoint}`, changed, { withCredentials: true });
      setParams(res.data);
      setOriginal(res.data);
      toast.success('Cambios guardados');
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!window.confirm('¿Restablecer valores predeterminados de esta subpestaña?')) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/settings/${endpoint}/reset`, {}, { withCredentials: true });
      setParams(res.data);
      setOriginal(res.data);
      toast.success('Valores predeterminados restaurados');
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  if (loading || !params) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;
  }

  return (
    <div className="space-y-6" data-testid={`${testidPrefix}-section`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="heading-2 flex items-center gap-2"><SlidersHorizontal className="w-5 h-5 text-blue-600" /> {title}</h2>
          <p className="body-text mt-1">{subtitle}</p>
        </div>
        <div className="flex gap-3">
          <button onClick={handleReset} disabled={saving}
                  className="btn-secondary text-sm flex items-center gap-2 disabled:opacity-60"
                  data-testid={`${testidPrefix}-reset`}>
            <RotateCcw className="w-4 h-4" /> Restablecer valores predeterminados
          </button>
          <button onClick={handleSave} disabled={!dirty || saving}
                  className="btn-primary flex items-center gap-2 disabled:opacity-60"
                  data-testid={`${testidPrefix}-save`}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <AdjBlock title="Planta y ascensor" testid={`${testidPrefix}-block-planta-ascensor`}>
          {FLOOR_ROWS.map(r => (
            <AdjRow key={r.key} label={r.label} value={params[r.key]}
                    onChange={(v) => setField(r.key, v)}
                    testid={`${testidPrefix}-${r.key}`} />
          ))}
          <p className="text-xs text-slate-400 pt-2 border-t border-slate-100 mt-2">
            {'"No aplica" y "Sin información" siempre aplican 0 %. La planta y el ascensor se evalúan conjuntamente.'}
          </p>
        </AdjBlock>

        <AdjBlock title="Piscina y garaje" testid={`${testidPrefix}-block-piscina-garaje`}>
          <AdjRow label="Piscina" value={params.pool}
                  onChange={(v) => setField('pool', v)}
                  testid={`${testidPrefix}-pool`} />
          <AdjRow label="Garaje incluido (plaza incluida)" value={params.garage}
                  onChange={(v) => setField('garage', v)}
                  testid={`${testidPrefix}-garage`} />
          <p className="text-xs text-slate-400 pt-2 border-t border-slate-100 mt-2">
            {'Sólo aplica cuando la característica es "Sí". "No" y "Sin información" aplican 0 %.'}
          </p>
        </AdjBlock>

        <AdjBlock title="Estado del inmueble" testid={`${testidPrefix}-block-estado`}>
          {CONDITION_ROWS.map(r => (
            <AdjRow key={r.key} label={r.label} value={params[r.key]}
                    onChange={(v) => setField(r.key, v)}
                    testid={`${testidPrefix}-${r.key}`} />
          ))}
          <p className="text-xs text-slate-400 pt-2 border-t border-slate-100 mt-2">
            {'"Sin información" aplica 0 %. Dato común guardado en property_features.condition.'}
          </p>
        </AdjBlock>

        <AdjBlock title="Límites" testid={`${testidPrefix}-block-limites`}>
          <AdjRow label="Bonificación máxima total" value={params.max_bonus}
                  onChange={(v) => setField('max_bonus', v)}
                  testid={`${testidPrefix}-max-bonus`} />
          <AdjRow label="Penalización máxima total" value={params.max_penalty}
                  onChange={(v) => setField('max_penalty', v)}
                  testid={`${testidPrefix}-max-penalty`} />
          <p className="text-xs text-slate-400 pt-2 border-t border-slate-100 mt-2">
            El ajuste total no podrá superar estos límites.
          </p>
        </AdjBlock>

        {variant === 'rental' && (
          <AdjBlock title="Coeficiente de prudencia" testid={`${testidPrefix}-block-prudencia`}>
            <AdjRow label="Coeficiente de prudencia del alquiler"
                    value={params.prudence_percentage}
                    onChange={(v) => setField('prudence_percentage', v)}
                    testid={`${testidPrefix}-prudence`}
                    help="Se aplica una sola vez sobre el precio de alquiler ajustado." />
          </AdjBlock>
        )}
      </div>
    </div>
  );
};

const SaleAdjustmentsSection = () => (
  <AdjustmentsSection
    title="Parámetros de VENTA"
    subtitle="Ponderaciones aplicadas sobre las características objetivas comunes (property_features)."
    endpoint="sale-adjustments"
    testidPrefix="sale-adj"
    variant="sale"
  />
);

const RentalAdjustmentsSection = () => (
  <AdjustmentsSection
    title="Parámetros de ALQUILER"
    subtitle="Ponderaciones aplicadas sobre las características objetivas comunes (property_features)."
    endpoint="rental-adjustments"
    testidPrefix="rental-adj"
    variant="rental"
  />
);


// ---- Revisiones: plazos por prioridad + escalado ----
// Configuración global de los plazos que definen si una solicitud de revisión
// está "en plazo" o "vencida". Solo superadmin. Los plazos se aplican al
// crear la solicitud y también al cambiar la prioridad (recomputa deadline
// desde la fecha de CREACIÓN original — nunca reinicia el reloj).
const ReviewDeadlinesSection = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alta, setAlta] = useState('3');
  const [media, setMedia] = useState('5');
  const [baja, setBaja] = useState('7');
  const [escalate, setEscalate] = useState(true);
  const [orig, setOrig] = useState({ alta: '3', media: '5', baja: '7', escalate: true });

  useEffect(() => {
    axios.get(`${API}/settings/review-deadlines`, { withCredentials: true })
      .then((res) => {
        const a = String(res.data?.alta_days ?? 3);
        const m = String(res.data?.media_days ?? 5);
        const b = String(res.data?.baja_days ?? 7);
        const e = Boolean(res.data?.escalate_to_superadmin);
        setAlta(a); setMedia(m); setBaja(b); setEscalate(e);
        setOrig({ alta: a, media: m, baja: b, escalate: e });
      })
      .catch((err) => toast.error(errMsg(err)))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    const parseDays = (v, label) => {
      const n = Number(v);
      if (!Number.isFinite(n) || n <= 0 || n > 90) {
        toast.error(`${label}: debe ser un número entero entre 1 y 90 días`);
        return null;
      }
      return Math.round(n);
    };
    const A = parseDays(alta, 'Alta'); if (A === null) return;
    const M = parseDays(media, 'Normal'); if (M === null) return;
    const B = parseDays(baja, 'Baja'); if (B === null) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/settings/review-deadlines`,
        { alta_days: A, media_days: M, baja_days: B, escalate_to_superadmin: escalate },
        { withCredentials: true });
      const a = String(res.data?.alta_days ?? A);
      const m = String(res.data?.media_days ?? M);
      const b = String(res.data?.baja_days ?? B);
      const e = Boolean(res.data?.escalate_to_superadmin);
      setAlta(a); setMedia(m); setBaja(b); setEscalate(e);
      setOrig({ alta: a, media: m, baja: b, escalate: e });
      toast.success('Plazos de revisión guardados');
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!window.confirm('¿Restaurar los plazos por defecto (3 · 5 · 7 días)?')) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/settings/review-deadlines/reset`, {}, { withCredentials: true });
      const a = String(res.data?.alta_days ?? 3);
      const m = String(res.data?.media_days ?? 5);
      const b = String(res.data?.baja_days ?? 7);
      const e = Boolean(res.data?.escalate_to_superadmin);
      setAlta(a); setMedia(m); setBaja(b); setEscalate(e);
      setOrig({ alta: a, media: m, baja: b, escalate: e });
      toast.success('Plazos restaurados');
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setSaving(false); }
  };

  const handleScanNow = async () => {
    try {
      const res = await axios.post(`${API}/notifications/scan-review-overdue`, {}, { withCredentials: true });
      toast.success(`Escaneadas ${res.data?.scanned ?? 0} · Escaladas ${res.data?.escalated ?? 0}`);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;
  }

  const dirty = alta !== orig.alta || media !== orig.media || baja !== orig.baja || escalate !== orig.escalate;

  return (
    <div className="space-y-6" data-testid="settings-revisiones-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="heading-2 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-blue-600" /> Plazos de "Solicitud de revisión"
          </h2>
          <p className="body-text mt-1">
            Días naturales dentro de los cuales una revisión debe resolverse según su prioridad.
            El plazo se cuenta desde la fecha de creación de la solicitud — si cambia la prioridad,
            el vencimiento se recalcula con el nuevo plazo pero mantiene la fecha original de creación.
            Cuando el plazo expira, la revisión aparece marcada como <span className="font-semibold text-red-600">VENCIDA</span> y — si el escalado está activo — el superadmin recibe una notificación push.
          </p>
        </div>
        <div className="flex gap-3">
          <button onClick={handleScanNow} className="btn-secondary text-sm flex items-center gap-2" data-testid="review-deadlines-scan-now">
            <RefreshCw className="w-4 h-4" /> Escanear ahora
          </button>
          <button
            onClick={handleReset}
            disabled={saving}
            className="btn-secondary text-sm flex items-center gap-2 disabled:opacity-60"
            data-testid="review-deadlines-reset"
          >
            <RotateCcw className="w-4 h-4" /> Restaurar defecto
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !dirty}
            className="btn-primary flex items-center gap-2 disabled:opacity-60"
            data-testid="review-deadlines-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl">
        <PriorityBox
          label="Prioridad ALTA"
          hint="Debe resolverse cuanto antes"
          color="border-red-400"
          textColor="text-red-600"
          value={alta}
          onChange={setAlta}
          testid="review-deadlines-alta"
        />
        <PriorityBox
          label="Prioridad NORMAL"
          hint="Plazo estándar"
          color="border-amber-400"
          textColor="text-amber-600"
          value={media}
          onChange={setMedia}
          testid="review-deadlines-media"
        />
        <PriorityBox
          label="Prioridad BAJA"
          hint="Puede esperar"
          color="border-slate-300"
          textColor="text-slate-500"
          value={baja}
          onChange={setBaja}
          testid="review-deadlines-baja"
        />
      </div>

      <div className="card-base p-5 max-w-4xl">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={escalate}
            onChange={(e) => setEscalate(e.target.checked)}
            className="mt-1 cursor-pointer"
            data-testid="review-deadlines-escalate"
          />
          <span>
            <span className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              Escalar a superadmin cuando venza el plazo
            </span>
            <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
              Envía una notificación push al superadmin y añade una traza a la nota del activo cuando la solicitud supera el plazo definido para su prioridad.
            </p>
          </span>
        </label>
      </div>
    </div>
  );
};

const PriorityBox = ({ label, hint, color, textColor, value, onChange, testid }) => (
  <div className={`card-base p-5 border-l-4 ${color}`}>
    <h3 className={`text-sm font-semibold uppercase tracking-wide ${textColor}`}>{label}</h3>
    <p className="text-xs mt-1 mb-4" style={{ color: 'var(--color-text-muted)' }}>{hint}</p>
    <div className="flex items-center gap-3">
      <input
        type="number"
        min={1}
        max={90}
        step={1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input-base w-24 text-sm text-right tabular-nums"
        data-testid={`${testid}-input`}
      />
      <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
        {Number(value) === 1 ? 'día' : 'días'}
      </span>
    </div>
  </div>
);


// ---- Datos: limpieza de duplicados ----
const DataHygieneSection = () => {
  const [stats, setStats] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);

  const loadStats = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/assets/duplicates-by-rc`, { withCredentials: true });
      setStats(r.data);
    } catch (err) {
      toast.error('Error: ' + (errMsg(err)));
    } finally { setLoading(false); }
  };

  useEffect(() => { loadStats(); }, []);

  const runPreview = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/assets/merge-duplicates-by-rc?dry_run=true`, {}, { withCredentials: true });
      setPreview(r.data);
    } catch (err) {
      toast.error('Error: ' + (errMsg(err)));
    } finally { setLoading(false); }
  };

  const runMerge = async () => {
    if (!window.confirm(`¿Fusionar ${stats?.num_grupos} grupos de duplicados? Los duplicados irán a Papelera.`)) return;
    setMerging(true);
    try {
      const r = await axios.post(`${API}/assets/merge-duplicates-by-rc`, {}, { withCredentials: true, timeout: 600000 });
      toast.success(`Fusionados ${r.data.maestros} maestros, ${r.data.fusionados} a Papelera`);
      setPreview(r.data);
      await loadStats();
    } catch (err) {
      toast.error('Error: ' + (errMsg(err)));
    } finally { setMerging(false); }
  };

  // Resync button — re-applies the current `_sum_deudas` rule
  // (deuda_total=SUM, deuda_reclamada=MAX) to all assets with multi-debt,
  // then triggers a global score recompute. Idempotent. Useful right after
  // deploying a debt-rollup rule change to production.
  const [resyncing, setResyncing] = useState(false);
  const runResyncDeudas = async () => {
    if (!window.confirm('Recalcula deuda_total (suma) y deuda_reclamada (MAX) para todos los activos con múltiples deudas, y luego ROI/Score global. Es seguro y reversible. ¿Continuar?')) return;
    setResyncing(true);
    try {
      const r1 = await axios.post(`${API}/assets/recompute-merged-reclamada`, {}, { withCredentials: true, timeout: 600000 });
      const r2 = await axios.post(`${API}/assets/recalculate-scores`, {}, { withCredentials: true, timeout: 600000 });
      toast.success(`Deudas: ${r1.data.updated}/${r1.data.total_scanned} actualizadas · Scores: ${r2.data.recalculated} recalculados`);
    } catch (err) {
      toast.error('Error: ' + (errMsg(err)));
    } finally { setResyncing(false); }
  };

  return (
    <div className="space-y-6" data-testid="data-hygiene-section">
      <div>
        <h2 className="heading-2 flex items-center gap-2"><Copy className="w-5 h-5 text-blue-600" /> Limpieza de datos</h2>
        <p className="body-text mt-1">Detecta y fusiona activos duplicados por referencia catastral. Los duplicados se mueven a Papelera y sus deudas pasan como ítems del activo maestro.</p>
      </div>

      <div className="card-base p-6">
        <h3 className="heading-3 mb-4">Duplicados por referencia catastral</h3>
        {loading && !stats ? (
          <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Cargando...</div>
        ) : stats ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
              <Kpi label="Grupos con duplicados" value={stats.num_grupos} />
              <Kpi label="Activos duplicados" value={stats.total_duplicados_a_fusionar} />
              <Kpi label="Total referencias" value={(stats.groups || []).length} />
            </div>
            <div className="flex gap-3 flex-wrap">
              <button onClick={runPreview} disabled={loading} className="btn-secondary text-sm flex items-center gap-2" data-testid="preview-merge-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <SlidersHorizontal className="w-4 h-4" />}
                Previsualizar fusión (sin cambios)
              </button>
              <button onClick={runMerge} disabled={merging || stats.num_grupos === 0} className="btn-primary text-sm flex items-center gap-2" data-testid="run-merge-btn">
                {merging ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Fusionar ahora ({stats.num_grupos} grupos)
              </button>
              <button
                onClick={runResyncDeudas}
                disabled={resyncing}
                className="btn-secondary text-sm flex items-center gap-2"
                title="Reaplica la regla deuda_total=SUMA / deuda_reclamada=MAX a los activos con varias deudas y recalcula ROI/Score globales. Idempotente."
                data-testid="resync-deudas-btn"
              >
                {resyncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                {resyncing ? 'Sincronizando...' : 'Sincronizar deudas y scoring'}
              </button>
            </div>
          </>
        ) : (
          <p className="text-slate-500">Sin datos.</p>
        )}
      </div>

      {preview && (
        <div className="card-base p-6">
          <h3 className="heading-3 mb-4">{preview.dry_run ? 'Previsualización' : 'Resultado'} ({preview.grupos} grupos)</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-4">Maestro</th>
                  <th className="py-2 pr-4">Ref. Catastral</th>
                  <th className="py-2 pr-4 text-right">Nº deudas</th>
                  <th className="py-2 pr-4 text-right">Deuda total</th>
                  <th className="py-2 pr-4">Duplicados → papelera</th>
                </tr>
              </thead>
              <tbody>
                {(preview.detalles || []).slice(0, 50).map((d, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-2 pr-4 font-medium text-blue-600">{d.master_npl || d.master_id?.slice(0, 8)}</td>
                    <td className="py-2 pr-4 font-mono text-xs">{d.referencia_catastral}</td>
                    <td className="py-2 pr-4 text-right">{d.num_deudas_resultado}</td>
                    <td className="py-2 pr-4 text-right">{d.deuda_total ? new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(d.deuda_total) : '-'}</td>
                    <td className="py-2 pr-4 text-xs text-slate-500">{(d.duplicados_npl || []).slice(0, 3).join(', ')}{(d.duplicados_npl || []).length > 3 ? `, +${d.duplicados_npl.length - 3} más` : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(preview.detalles || []).length > 50 && (
              <p className="text-xs text-slate-500 mt-2">Mostrando 50 de {preview.detalles.length} grupos.</p>
            )}
          </div>
        </div>
      )}

      <FusionadosPanel />
      <FaseMisplacedPanel />
      <RepairMarketPricesProvincesPanel />
      <FaseJudicialAliasesPanel />
    </div>
  );
};

// ---- Fusionados (activos duplicados ya movidos a papelera) ----
const FusionadosPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [purging, setPurging] = useState(false);
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState({}); // {id: true}
  const limit = 50;

  const load = React.useCallback(async (off = 0) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/assets/fusionados?limit=${limit}&offset=${off}`, { withCredentials: true });
      setData(r.data);
      setOffset(off);
      setSelected({});
    } catch (err) {
      toast.error('Error: ' + (errMsg(err)));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(0); }, [load]);

  const purgeAll = async () => {
    if (!data || !data.total) return;
    if (!window.confirm(`¿Eliminar DEFINITIVAMENTE los ${data.total} activos fusionados? Esta acción es irreversible. Los datos ya están preservados en las deudas de los activos maestros.`)) return;
    setPurging(true);
    try {
      const r = await axios.post(`${API}/assets/fusionados/purge`, {}, { withCredentials: true, timeout: 600000 });
      toast.success(`${r.data.deleted} activos fusionados eliminados definitivamente`);
      await load(0);
    } catch (err) {
      toast.error('Error: ' + (errMsg(err)));
    } finally { setPurging(false); }
  };

  const purgeSelected = async () => {
    const ids = Object.keys(selected).filter(k => selected[k]);
    if (!ids.length) { toast.error('Selecciona al menos un activo'); return; }
    if (!window.confirm(`¿Eliminar DEFINITIVAMENTE los ${ids.length} activos seleccionados?`)) return;
    setPurging(true);
    try {
      const r = await axios.post(`${API}/assets/fusionados/purge`, { ids }, { withCredentials: true, timeout: 600000 });
      toast.success(`${r.data.deleted} activos eliminados`);
      await load(offset);
    } catch (err) {
      toast.error('Error: ' + (errMsg(err)));
    } finally { setPurging(false); }
  };

  const toggleAll = (items) => {
    const allSelected = items.every(i => selected[i.id]);
    const next = { ...selected };
    for (const i of items) next[i.id] = !allSelected;
    setSelected(next);
  };

  const numSelected = Object.values(selected).filter(Boolean).length;

  if (!data && loading) {
    return (
      <div className="card-base p-6">
        <h3 className="heading-3 mb-2">Activos fusionados</h3>
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Cargando...</div>
      </div>
    );
  }

  if (data && data.total === 0) {
    return (
      <div className="card-base p-6" data-testid="fusionados-panel">
        <h3 className="heading-3 mb-2">Activos fusionados</h3>
        <p className="text-sm text-slate-500">No hay activos fusionados pendientes de eliminar.</p>
      </div>
    );
  }

  const items = data?.items || [];
  const hasMore = data && offset + items.length < data.total;
  return (
    <div className="card-base p-6" data-testid="fusionados-panel">
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div>
          <h3 className="heading-3 flex items-center gap-2"><Trash2 className="w-5 h-5 text-red-600" /> Activos fusionados</h3>
          <p className="body-text mt-1 text-sm">
            Estos activos fueron movidos a Papelera tras la fusión por referencia catastral. Sus deudas ya están integradas en los activos maestros. Aquí puedes <strong>eliminarlos definitivamente</strong>.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={purgeSelected} disabled={purging || numSelected === 0} className="btn-secondary text-sm flex items-center gap-2 border-red-200 text-red-700 hover:bg-red-50" data-testid="purge-selected-btn">
            <Trash2 className="w-4 h-4" /> Eliminar seleccionados ({numSelected})
          </button>
          <button onClick={purgeAll} disabled={purging || !data?.total} className="btn-primary text-sm flex items-center gap-2 bg-red-600 hover:bg-red-700" data-testid="purge-all-btn">
            {purging ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Eliminar TODOS ({data?.total || 0})
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-4 w-8">
                <input type="checkbox" checked={items.length > 0 && items.every(i => selected[i.id])} onChange={() => toggleAll(items)} className="w-4 h-4 accent-blue-600" />
              </th>
              <th className="py-2 pr-4">Duplicado (NPL)</th>
              <th className="py-2 pr-4">Ref. Catastral</th>
              <th className="py-2 pr-4">Dirección</th>
              <th className="py-2 pr-4">Municipio</th>
              <th className="py-2 pr-4">Proveedor</th>
              <th className="py-2 pr-4">→ Fusionado en</th>
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`fusionado-row-${a.id}`}>
                <td className="py-2 pr-4">
                  <input type="checkbox" checked={!!selected[a.id]} onChange={(e) => setSelected(p => ({ ...p, [a.id]: e.target.checked }))} className="w-4 h-4 accent-blue-600" data-testid={`fusionado-check-${a.id}`} />
                </td>
                <td className="py-2 pr-4 font-medium">{a.codigo_npl || a.id.slice(0, 8)}</td>
                <td className="py-2 pr-4 font-mono text-xs">{a.referencia_catastral || '-'}</td>
                <td className="py-2 pr-4 max-w-xs truncate" title={a.direccion}>{a.direccion || '-'}</td>
                <td className="py-2 pr-4">{a.municipio || '-'}</td>
                <td className="py-2 pr-4 text-xs text-slate-500">{a.proveedor || '-'}</td>
                <td className="py-2 pr-4 text-blue-600 font-medium">{a.master_codigo_npl || a.fusionado_en?.slice(0, 8)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-4 text-sm">
        <span className="text-slate-500">Mostrando {offset + 1}-{offset + items.length} de {data?.total || 0}</span>
        <div className="flex gap-2">
          <button onClick={() => load(Math.max(0, offset - limit))} disabled={loading || offset === 0} className="btn-secondary text-xs">← Anterior</button>
          <button onClick={() => load(offset + limit)} disabled={loading || !hasMore} className="btn-secondary text-xs">Siguiente →</button>
        </div>
      </div>
    </div>
  );
};

const Kpi = ({ label, value }) => (
  <div className="bg-slate-50 rounded-lg p-4">
    <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
    <p className="text-2xl font-semibold text-slate-900 mt-1">{value ?? '-'}</p>
  </div>
);

// ---- Reparar `fase` mal asignado (valores que en realidad son fase_judicial) ----
const FaseMisplacedPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/fase-judicial/misplaced`, { withCredentials: true });
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cargar previsualización');
    } finally { setLoading(false); }
  };

  const runFix = async () => {
    if (!data || data.moveable === 0) return;
    if (!window.confirm(`Procesar ${data.moveable} activos. ${data.unmappable} no se podrán mover (necesitan alias). ¿Continuar?`)) return;
    setRunning(true);
    try {
      const r = await axios.post(`${API}/fase-judicial/fix-misplaced`, {}, { withCredentials: true });
      const msg = `Migrados ${r.data.moved} CRÉDITO · Limpiados ${r.data.cleared_non_credito || 0} no-CRÉDITO · ${r.data.skipped_unmappable || 0} sin alias`;
      toast.success(msg);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al migrar');
    } finally { setRunning(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="card-base p-6 space-y-4 mt-6" data-testid="fase-misplaced-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="heading-2 flex items-center gap-2"><Database className="w-5 h-5 text-amber-600" /> Reparar campo "Fase" mal asignado</h2>
          <p className="body-text mt-1">Detecta activos cuyo campo <strong>Fase</strong> contiene un valor que pertenece a <strong>Fase Judicial</strong> (ej: "Recibida certificación registral"). Los movemos al campo correcto y vaciamos el original.</p>
        </div>
        <button onClick={load} disabled={loading} className="btn-secondary text-sm flex items-center gap-2" data-testid="fase-misplaced-reload">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
          Recargar
        </button>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <Kpi label="Total con fase no canónica" value={data.total} />
            <Kpi label="Migrables (con alias)" value={data.moveable} />
            <Kpi label="Sin alias (revisión manual)" value={data.unmappable} />
          </div>
          {data.moveable > 0 && (
            <button onClick={runFix} disabled={running} className="btn-primary text-sm flex items-center gap-2" data-testid="fase-misplaced-run">
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Migrar {data.moveable} activos automáticamente
            </button>
          )}
          {data.items && data.items.length > 0 && (
            <div className="overflow-x-auto border border-slate-200 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">NPL</th>
                    <th className="px-3 py-2 text-left">Tipo Op.</th>
                    <th className="px-3 py-2 text-left">Fase actual (incorrecto)</th>
                    <th className="px-3 py-2 text-left">Fase Judicial actual</th>
                    <th className="px-3 py-2 text-left">Se moverá a</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.slice(0, 100).map(it => (
                    <tr key={it.id} className="border-t border-slate-100" data-testid={`fase-misplaced-row-${it.id}`}>
                      <td className="px-3 py-2 font-mono text-xs">{it.codigo_npl || '-'}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{it.tipo_activo_operativo || '-'}</td>
                      <td className="px-3 py-2 text-xs text-amber-700" title={it.fase_actual}>{(it.fase_actual || '').slice(0, 50)}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{it.fase_judicial_actual || '—'}</td>
                      <td className="px-3 py-2 text-xs">
                        {it.action === 'clear_fase' ? (
                          <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium" title="No-CRÉDITO: se borrará el valor del campo fase">vaciar fase (no-CRÉDITO)</span>
                        ) : it.would_move_to ? (
                          <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">{it.would_move_to}</span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700 font-medium">sin alias</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.items.length > 100 && (
                <p className="text-xs text-slate-500 p-2">Mostrando 100 de {data.items.length}.</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ---- Reparar provincias incorrectas en market_prices ----
// Recorre `market_prices`, resuelve la provincia correcta consultando la
// tabla maestra `price_m2_base` (por municipio + alias bilingües) y aplica
// UPDATE cuando la provincia almacenada no coincide con la canónica. También
// normaliza variantes tipo "Alacant Provincia" → "Alicante".
const RepairMarketPricesProvincesPanel = () => {
  const [preview, setPreview] = useState(null);
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(false);
  const [diagLoading, setDiagLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const runDiagnose = async () => {
    setDiagLoading(true);
    try {
      const r = await axios.get(`${API}/market-prices/diagnose`,
        { withCredentials: true, timeout: 120000 });
      setDiag(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al ejecutar diagnóstico');
    } finally { setDiagLoading(false); }
  };

  const runDryRun = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/market-prices/repair-provinces`,
        { dry_run: true }, { withCredentials: true, timeout: 120000 });
      setPreview(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al ejecutar dry-run');
    } finally { setLoading(false); }
  };

  const runApply = async () => {
    if (!preview) return;
    const hasWork = (preview.to_correct || 0) > 0
      || (preview.duplicates_to_remove || 0) > 0
      || (preview.bilingual_muni_renames || 0) > 0
      || (preview.dedup2_to_remove || 0) > 0
      || (preview.master_merges_planned || 0) > 0;
    if (!hasWork) return;
    const parts = [];
    if (preview.to_correct > 0) parts.push(`${preview.to_correct} correcciones de provincia`);
    if (preview.duplicates_to_remove > 0) parts.push(`${preview.duplicates_to_remove} duplicados a eliminar`);
    if (preview.bilingual_muni_renames > 0) parts.push(`${preview.bilingual_muni_renames} renombres bilingües en market_prices`);
    if (preview.dedup2_to_remove > 0) parts.push(`${preview.dedup2_to_remove} duplicados extra post-rename`);
    if (preview.master_merges_planned > 0) parts.push(`${preview.master_merges_planned} fusiones bilingües en tabla maestra`);
    if (!window.confirm(`¿Aplicar los siguientes cambios?\n\n${parts.join('\n')}`)) return;
    setApplying(true);
    try {
      const r = await axios.post(`${API}/market-prices/repair-provinces`,
        { dry_run: false }, { withCredentials: true, timeout: 300000 });
      toast.success(`${r.data.modified} corregidos · ${r.data.duplicates_removed} duplicados eliminados`);
      setPreview(r.data);
      // Refresh diagnóstico automáticamente para mostrar el estado post-apply
      runDiagnose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al aplicar correcciones');
    } finally { setApplying(false); }
  };

  return (
    <div className="card-base p-6 space-y-4 mt-6" data-testid="repair-market-provinces-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="heading-2 flex items-center gap-2">
            <Database className="w-5 h-5 text-amber-600" /> Reparar provincias incorrectas
          </h2>
          <p className="body-text mt-1">
            Recorre <strong>market_prices</strong> (Fotocasa) y, para cada registro, resuelve la provincia
            correcta consultando la <strong>tabla maestra</strong> (Idealista / <code>price_m2_base</code>) por
            municipio y alias bilingües. También normaliza variantes no canónicas como
            <em> &laquo;Alacant Provincia&raquo;</em> → <em>Alicante</em>. Solo hace <strong>UPDATE</strong>, nunca borra.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={runDiagnose} disabled={diagLoading} className="btn-secondary text-sm flex items-center gap-2" data-testid="repair-market-provinces-diagnose">
            {diagLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Info className="w-4 h-4" />}
            {diagLoading ? 'Diagnosticando…' : 'Diagnóstico previo'}
          </button>
          <button onClick={runDryRun} disabled={loading} className="btn-secondary text-sm flex items-center gap-2" data-testid="repair-market-provinces-dryrun">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
            {loading ? 'Analizando…' : 'Analizar (dry-run)'}
          </button>
        </div>
      </div>

      {/* ---------- DIAGNÓSTICO PREVIO ---------- */}
      {diag && (
        <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 space-y-4" data-testid="repair-diag-block">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Diagnóstico ({diag.total_docs} registros)</h3>

          {/* provincias distinct */}
          <div>
            <p className="text-xs font-medium text-slate-600 mb-1">(1) Valores distintos de <code>province_name</code>:</p>
            <div className="overflow-x-auto border border-slate-200 rounded bg-white">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-1.5 text-left">Provincia almacenada</th>
                    <th className="px-3 py-1.5 text-right">Registros</th>
                  </tr>
                </thead>
                <tbody>
                  {diag.provinces.map((p, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-3 py-1.5 text-xs font-mono">{p.province_name || '(vacío)'}</td>
                      <td className="px-3 py-1.5 text-xs text-right font-semibold">{p.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* muni == prov */}
          <div>
            <p className="text-xs font-medium text-slate-600 mb-1">
              (2) Registros con <code>municipality_name == province_name</code> (posibles barrios mal clasificados):
              <span className="ml-2 text-slate-900 font-bold">{diag.muni_equals_prov.reduce((a, r) => a + r.count, 0)} registros</span>
            </p>
            {diag.muni_equals_prov.length > 0 && (
              <div className="overflow-x-auto border border-slate-200 rounded bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-1.5 text-left">Municipio</th>
                      <th className="px-3 py-1.5 text-left">Provincia</th>
                      <th className="px-3 py-1.5 text-right">Registros</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diag.muni_equals_prov.map((r, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-3 py-1.5 text-xs">{r.municipality_name || '—'}</td>
                        <td className="px-3 py-1.5 text-xs font-mono text-amber-700">{r.province_name || '—'}</td>
                        <td className="px-3 py-1.5 text-xs text-right font-semibold">{r.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* provincias sospechosas */}
          {diag.suspicious_provinces?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-slate-600 mb-1">
                (3) Provincias con nombre no canónico — desglose por municipio:
              </p>
              {diag.suspicious_provinces.map((s, si) => (
                <div key={si} className="mb-3 border border-amber-200 rounded p-2 bg-amber-50/40">
                  <p className="text-xs font-semibold text-amber-800">
                    <code>province_name = &laquo;{s.province_name}&raquo;</code> · {s.total} registros
                  </p>
                  {s.muni_matches_province?.length > 0 && (
                    <div className="mt-1">
                      <p className="text-[11px] font-medium text-emerald-800">(a) Municipio coherente con la provincia (solo provincia mal) — {s.muni_matches_province.reduce((a, r) => a + r.count, 0)} registros:</p>
                      <ul className="ml-4 text-[11px] text-slate-700">
                        {s.muni_matches_province.map((r, ri) => (
                          <li key={ri}>{r.count} × {r.municipality_name} {r.neighborhood_name ? `· ${r.neighborhood_name}` : ''}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {s.muni_differs_from_province?.length > 0 && (
                    <div className="mt-1">
                      <p className="text-[11px] font-medium text-red-700">(b) Municipios distintos mal etiquetados con esta provincia — {s.muni_differs_from_province.reduce((a, r) => a + r.count, 0)} registros:</p>
                      <ul className="ml-4 text-[11px] text-slate-700">
                        {s.muni_differs_from_province.slice(0, 15).map((r, ri) => (
                          <li key={ri}>{r.count} × {r.municipality_name} {r.neighborhood_name ? `· ${r.neighborhood_name}` : ''}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {preview && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Kpi label="Total escaneados" value={preview.total_scanned} />
            <Kpi label="A corregir" value={preview.to_correct} />
            <Kpi label="Corregidos" value={preview.modified} />
            <Kpi label="Duplicados detectados" value={preview.duplicates_to_remove || 0} />
            <Kpi label="Municipios sin match" value={preview.unresolvable?.length || 0} />
          </div>

          {/* KPIs de consolidación bilingüe (paso nuevo) */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Kpi label="Renombres bilingües (mp)" value={preview.dry_run ? preview.bilingual_muni_renames : preview.bilingual_muni_renamed} />
            <Kpi label="Dedup post-rename (mp)" value={preview.dry_run ? preview.dedup2_to_remove : preview.dedup2_removed} />
            <Kpi label="Maestra: fusiones plan." value={preview.master_merges_planned || 0} />
            <Kpi label="Maestra: entradas fusionadas" value={preview.master_merged || 0} />
            <Kpi label="Maestra: eliminadas" value={preview.master_removed || 0} />
          </div>

          {(preview.to_correct > 0 || preview.duplicates_to_remove > 0 || preview.bilingual_muni_renames > 0 || preview.dedup2_to_remove > 0 || preview.master_merges_planned > 0) && preview.modified === 0 && preview.duplicates_removed === 0 && preview.bilingual_muni_renamed === 0 && (
            <button onClick={runApply} disabled={applying} className="btn-primary text-sm flex items-center gap-2" data-testid="repair-market-provinces-apply">
              {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Aplicar todos los cambios
            </button>
          )}

          {preview.duplicates_removed > 0 && (
            <p className="text-sm text-emerald-700 font-medium">
              ✓ {preview.modified} registros corregidos · {preview.duplicates_removed} duplicados eliminados
              {preview.bilingual_muni_renamed > 0 && ` · ${preview.bilingual_muni_renamed} renombres bilingües`}
              {preview.dedup2_removed > 0 && ` · ${preview.dedup2_removed} extra duplicados`}
              {preview.master_merged > 0 && ` · ${preview.master_merged} maestros fusionados`}
              {preview.master_removed > 0 && ` (${preview.master_removed} eliminados)`}
            </p>
          )}

          {preview.by_province && preview.by_province.length > 0 && (
            <div className="overflow-x-auto border border-slate-200 rounded-lg">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 px-3 pt-2">
                Resumen por provincia
              </h3>
              <table className="w-full text-sm mt-1">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left">De</th>
                    <th className="px-3 py-2 text-left">A</th>
                    <th className="px-3 py-2 text-right">Registros</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.by_province.map((t, i) => (
                    <tr key={i} className="border-t border-slate-100" data-testid={`repair-transition-${i}`}>
                      <td className="px-3 py-2 text-xs text-red-700 font-mono">{t.from || '—'}</td>
                      <td className="px-3 py-2 text-xs text-emerald-700 font-mono">{t.to || '—'}</td>
                      <td className="px-3 py-2 text-xs text-right font-semibold">{t.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {preview.duplicates_detail && preview.duplicates_detail.length > 0 && (
            <div className="overflow-x-auto border border-red-200 rounded-lg bg-red-50/30">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-red-700 px-3 pt-2">
                Duplicados a eliminar ({preview.duplicates_detail.length} grupos)
              </h3>
              <p className="text-[11px] text-red-700 px-3 mb-1">
                Se mantiene un único registro por (municipio + barrio + tipo) — se conserva el más reciente
                con provincia canónica; el resto se elimina.
              </p>
              <table className="w-full text-sm">
                <thead className="bg-red-50 text-xs uppercase tracking-wide text-red-700">
                  <tr>
                    <th className="px-3 py-2 text-left">Municipio</th>
                    <th className="px-3 py-2 text-left">Barrio</th>
                    <th className="px-3 py-2 text-left">Tipo</th>
                    <th className="px-3 py-2 text-left">Se conserva (provincia)</th>
                    <th className="px-3 py-2 text-right">A eliminar</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.duplicates_detail.slice(0, 100).map((g, i) => (
                    <tr key={i} className="border-t border-red-100" data-testid={`repair-duplicate-${i}`}>
                      <td className="px-3 py-2 text-xs">{g.municipality_name || '—'}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{g.neighborhood_name || '—'}</td>
                      <td className="px-3 py-2 text-xs font-mono">{g.price_type || '—'}</td>
                      <td className="px-3 py-2 text-xs text-emerald-700">{g.kept_province || '—'}</td>
                      <td className="px-3 py-2 text-xs text-right font-semibold text-red-700">{g.removed_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {preview.unresolvable && preview.unresolvable.length > 0 && (
            <div className="overflow-x-auto border border-amber-200 rounded-lg bg-amber-50/40">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-amber-700 px-3 pt-2">
                Municipios sin match en la tabla maestra ({preview.unresolvable.length})
              </h3>
              <p className="text-[11px] text-amber-700 px-3 mb-1">
                Estos municipios existen en <code>market_prices</code> pero no en <code>price_m2_base</code>.
                Se conservan sin cambios. Añádelos a la tabla maestra para poder repararlos en el próximo pase.
              </p>
              <table className="w-full text-sm">
                <thead className="bg-amber-50 text-xs uppercase tracking-wide text-amber-700">
                  <tr>
                    <th className="px-3 py-2 text-left">Municipio</th>
                    <th className="px-3 py-2 text-left">Provincia almacenada</th>
                    <th className="px-3 py-2 text-right">Registros</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.unresolvable.slice(0, 100).map((u, i) => (
                    <tr key={i} className="border-t border-amber-100" data-testid={`repair-unresolvable-${i}`}>
                      <td className="px-3 py-2 text-xs">{u.municipality_name}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{u.province_stored || '—'}</td>
                      <td className="px-3 py-2 text-xs text-right">{u.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};



// ---- Aliases custom de Fase Judicial (admin/gestor) ----
// Default canonical options (kept as fallback when API is loading). The
// effective list comes from /api/fase-judicial/options and includes any
// custom canonical values added via this very panel.
const FJ_DEFAULT_OPTIONS = [
  { v: 'sin_iniciar', l: 'Sin iniciar' },
  { v: 'demanda_presentada', l: 'Demanda presentada' },
  { v: 'auto_admision', l: 'Auto de admisión' },
  { v: 'certificacion_registral', l: 'Recibida certificación registral' },
  { v: 'subasta_solicitada', l: 'Subasta solicitada' },
  { v: 'subasta_convocada', l: 'Subasta convocada' },
  { v: 'subasta_en_curso', l: 'Subasta en curso' },
  { v: 'subasta_celebrada', l: 'Subasta celebrada' },
  { v: 'auto_adjudicacion', l: 'Auto de adjudicación' },
  { v: 'adjudicacion', l: 'Adjudicación' },
  { v: 'lanzamiento', l: 'Lanzamiento' },
  { v: 'oposicion', l: 'Oposición' },
  { v: 'apelacion', l: 'Apelación' },
  { v: 'cerrado', l: 'Cerrado' },
];

const FaseJudicialAliasesPanel = () => {
  const [aliases, setAliases] = useState([]);
  const [options, setOptions] = useState(FJ_DEFAULT_OPTIONS);
  const [customOptions, setCustomOptions] = useState([]);
  const [newAlias, setNewAlias] = useState('');
  const [newCanonical, setNewCanonical] = useState('subasta_convocada');
  const [saving, setSaving] = useState(false);
  // New canonical option (extends the dropdown). Hidden behind a [+] button.
  const [showAddCanonical, setShowAddCanonical] = useState(false);
  const [newOptionLabel, setNewOptionLabel] = useState('');
  const [savingOption, setSavingOption] = useState(false);
  // "Reevaluar fase judicial" — re-runs the backfill so newly added aliases
  // get applied to existing assets that still have raw `estado_procesal`.
  const [reevaluando, setReevaluando] = useState(false);
  // Search + filter by canonical. Lightweight client-side: the alias list
  // is small (rarely >200) so we can avoid backend pagination here.
  const [aliasSearch, setAliasSearch] = useState('');
  const [aliasCanonicalFilter, setAliasCanonicalFilter] = useState('');

  const reevaluarFaseJudicial = async () => {
    setReevaluando(true);
    try {
      const r = await axios.post(`${API}/assets/backfill-fase-judicial`, {}, { withCredentials: true, timeout: 120000 });
      const filled = r.data?.filled ?? 0;
      const skipped = r.data?.skipped_unknown ?? 0;
      if (filled > 0) {
        toast.success(`${filled} activos actualizados${skipped > 0 ? ` · ${skipped} aún sin alias` : ''}`);
      } else if (skipped > 0) {
        toast(`Sin cambios. ${skipped} activos siguen con valores no reconocidos — añade más aliases.`);
      } else {
        toast.success('Todo al día — no hay activos pendientes de mapear.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al reevaluar');
    } finally {
      setReevaluando(false);
    }
  };

  const load = async () => {
    try {
      const r = await axios.get(`${API}/fase-judicial/options`, { withCredentials: true });
      setAliases(r.data.aliases || []);
      setOptions(r.data.options || FJ_DEFAULT_OPTIONS);
      setCustomOptions(r.data.custom_options || []);
    } catch { /* noop */ }
  };

  useEffect(() => { load(); }, []);

  const addAlias = async () => {
    if (!newAlias.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/fase-judicial/aliases`, { alias: newAlias.trim(), canonical: newCanonical }, { withCredentials: true });
      toast.success('Alias añadido');
      setNewAlias('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally { setSaving(false); }
  };

  const deleteAlias = async (aliasNorm) => {
    if (!window.confirm(`Eliminar alias "${aliasNorm}"?`)) return;
    try {
      await axios.delete(`${API}/fase-judicial/aliases/${encodeURIComponent(aliasNorm)}`, { withCredentials: true });
      toast.success('Eliminado');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const addCanonicalOption = async () => {
    const label = newOptionLabel.trim();
    if (!label) return;
    // Auto-derive value from label: lowercase, remove accents, snake_case.
    const value = label
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
    if (!value) { toast.error('Etiqueta inválida'); return; }
    setSavingOption(true);
    try {
      const r = await axios.post(`${API}/fase-judicial/options`, { value, label }, { withCredentials: true });
      toast.success(`Fase añadida: "${label}"`);
      setNewOptionLabel('');
      setShowAddCanonical(false);
      // Auto-select the new option in the alias canonical select.
      setNewCanonical(r.data.value);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally { setSavingOption(false); }
  };

  const deleteCanonicalOption = async (value) => {
    if (!window.confirm(`Eliminar la fase custom "${value}"? Los aliases mapeados a ella también se borrarán.`)) return;
    try {
      await axios.delete(`${API}/fase-judicial/options/${encodeURIComponent(value)}`, { withCredentials: true });
      toast.success('Fase eliminada');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  return (
    <div className="card-base p-6 space-y-4 mt-6" data-testid="fj-aliases-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="heading-2 flex items-center gap-2"><SlidersHorizontal className="w-5 h-5 text-purple-600" /> Aliases de Fase Judicial</h2>
          <p className="body-text mt-1">Asocia textos libres del Excel (ej. "SUBASTA ELECTRÓNICA CONVOCADA") con un valor canónico de Fase Judicial. Se aplican automáticamente en futuras importaciones y al editar inline.</p>
        </div>
        <button
          onClick={reevaluarFaseJudicial}
          disabled={reevaluando}
          className="btn-secondary text-sm flex items-center gap-2 whitespace-nowrap"
          title="Aplica los aliases a los activos existentes con fase judicial sin mapear"
          data-testid="reevaluar-fase-judicial-btn"
        >
          {reevaluando
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <RefreshCw className="w-4 h-4" />}
          {reevaluando ? 'Reevaluando...' : 'Reevaluar fases pendientes'}
        </button>
      </div>

      {customOptions.length > 0 && (
        <div className="rounded-lg border bg-purple-50/40" style={{ borderColor: 'var(--color-border)' }}>
          <div className="px-3 py-2 text-xs font-semibold text-purple-700 uppercase tracking-wide border-b" style={{ borderColor: 'var(--color-border)' }}>
            Fases custom añadidas ({customOptions.length})
          </div>
          <ul className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
            {customOptions.map(o => (
              <li key={o.v} className="px-3 py-2 flex items-center justify-between text-sm" data-testid={`fj-custom-row-${o.v}`}>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium font-mono">{o.v}</span>
                  <span>{o.l}</span>
                </div>
                <button onClick={() => deleteCanonicalOption(o.v)} className="text-red-500 hover:text-red-700" title="Eliminar fase custom" data-testid={`fj-custom-delete-${o.v}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2 pt-2 border-t border-slate-100">
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs font-medium block mb-1 text-slate-600">Texto del Excel</label>
          <input
            type="text"
            value={newAlias}
            onChange={e => setNewAlias(e.target.value)}
            placeholder="Ej: SUBASTA ELECTRÓNICA CONVOCADA"
            className="input-base text-sm"
            data-testid="fj-alias-input"
          />
        </div>
        <div className="min-w-[180px]">
          <label className="text-xs font-medium block mb-1 text-slate-600 flex items-center gap-1.5">
            <span>Mapear a</span>
            <button
              type="button"
              onClick={() => setShowAddCanonical(v => !v)}
              className="text-purple-600 hover:text-purple-800"
              title="Añadir nueva fase al desplegable"
              data-testid="fj-add-option-btn"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </label>
          <select value={newCanonical} onChange={e => setNewCanonical(e.target.value)} className="input-base text-sm" data-testid="fj-alias-canonical">
            {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </div>
        <button onClick={addAlias} disabled={saving || !newAlias.trim()} className="btn-primary text-sm flex items-center gap-2" data-testid="fj-alias-add">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Añadir
        </button>
      </div>

      {showAddCanonical && (
        <div className="rounded-lg border-2 border-dashed p-3 flex flex-wrap items-end gap-2 bg-purple-50/40" style={{ borderColor: 'var(--color-border)' }} data-testid="fj-add-option-form">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs font-medium block mb-1 text-slate-600">Nueva fase judicial — etiqueta</label>
            <input
              type="text"
              value={newOptionLabel}
              onChange={e => setNewOptionLabel(e.target.value)}
              placeholder="Ej: Requerimiento de pago"
              className="input-base text-sm"
              data-testid="fj-add-option-label"
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCanonicalOption(); } }}
            />
          </div>
          <button onClick={addCanonicalOption} disabled={savingOption || !newOptionLabel.trim()} className="btn-primary text-sm flex items-center gap-2" data-testid="fj-add-option-save">
            {savingOption ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Crear fase
          </button>
          <button onClick={() => { setShowAddCanonical(false); setNewOptionLabel(''); }} className="btn-secondary text-sm" data-testid="fj-add-option-cancel">
            Cancelar
          </button>
        </div>
      )}

      {aliases.length > 0 && (
        <>
          {/* Search + filter bar — shown only when there are aliases.
               Helps when the list grows beyond a screenful. */}
          <div className="flex flex-wrap items-end gap-2 pt-3 border-t border-slate-100">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs font-medium block mb-1 text-slate-600">Buscar alias</label>
              <input
                type="text"
                value={aliasSearch}
                onChange={e => setAliasSearch(e.target.value)}
                placeholder="Texto del Excel o canónica..."
                className="input-base text-sm"
                data-testid="fj-alias-search"
              />
            </div>
            <div className="min-w-[180px]">
              <label className="text-xs font-medium block mb-1 text-slate-600">Filtrar por canónica</label>
              <select
                value={aliasCanonicalFilter}
                onChange={e => setAliasCanonicalFilter(e.target.value)}
                className="input-base text-sm"
                data-testid="fj-alias-canonical-filter"
              >
                <option value="">Todas</option>
                {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
            {(aliasSearch || aliasCanonicalFilter) && (
              <button
                onClick={() => { setAliasSearch(''); setAliasCanonicalFilter(''); }}
                className="btn-secondary text-xs"
                data-testid="fj-alias-filter-clear"
              >
                Limpiar
              </button>
            )}
          </div>
          {(() => {
            const q = aliasSearch.trim().toLowerCase();
            const filtered = aliases.filter(a =>
              (!q || a.alias.toLowerCase().includes(q) || a.canonical.toLowerCase().includes(q))
              && (!aliasCanonicalFilter || a.canonical === aliasCanonicalFilter)
            );
            if (filtered.length === 0) {
              return (
                <div className="text-center py-6 text-sm text-slate-500" data-testid="fj-alias-no-matches">
                  Sin coincidencias para los filtros aplicados.
                </div>
              );
            }
            return (
              <div className="overflow-x-auto border border-slate-200 rounded-lg" data-testid="fj-alias-table-wrap">
                <div className="px-3 py-2 text-xs text-slate-500 bg-slate-50 border-b border-slate-100">
                  Mostrando <strong>{filtered.length}</strong> de <strong>{aliases.length}</strong> aliases
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-2 text-left">Alias (normalizado)</th>
                      <th className="px-3 py-2 text-left">Canonical</th>
                      <th className="px-3 py-2 text-right w-24">—</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(a => (
                      <tr key={a.alias} className="border-t border-slate-100" data-testid={`fj-alias-row-${a.alias}`}>
                        <td className="px-3 py-2 font-mono text-xs">{a.alias}</td>
                        <td className="px-3 py-2"><span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium">{a.canonical}</span></td>
                        <td className="px-3 py-2 text-right">
                          <button onClick={() => deleteAlias(a.alias)} className="text-red-500 hover:text-red-700" title="Eliminar" data-testid={`fj-alias-delete-${a.alias}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </>
      )}
      {aliases.length === 0 && (
        <p className="text-sm text-slate-500 italic">Aún no hay aliases custom definidos. Los aliases por defecto del sistema (substring-based) siguen funcionando.</p>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Precios de Mercado (Fotocasa) — tabla municipal con venta y alquiler,
// histórico y importación manual. Fase 1: arquitectura + UI. La ingesta real
// depende del scraper Fotocasa (ver /app/backend/services/fotocasa.py).
// ---------------------------------------------------------------------------
const STATUS_STYLES = {
  VALID: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'VÁLIDO' },
  POSSIBLE_PROVINCIAL_FALLBACK: { cls: 'bg-amber-50 text-amber-700 border-amber-200', label: 'FALLBACK PROVINCIAL' },
  NO_MUNICIPAL_DATA: { cls: 'bg-red-50 text-red-700 border-red-200', label: 'SIN DATO MUNICIPAL' },
  SIN_DATO: { cls: 'bg-slate-100 text-slate-500 border-slate-200', label: 'SIN DATO' },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.SIN_DATO;
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${s.cls}`}>{s.label}</span>;
};

const MarketPricesSection = () => {
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(null); // {name, type}
  const [history, setHistory] = useState([]);
  // Modal de importación manual (pegado desde Fotocasa)
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteProvince, setPasteProvince] = useState(null); // {province_name, province_slug}
  const [pasteType, setPasteType] = useState('SALE');
  const [pasteText, setPasteText] = useState('');
  const [pastePricProv, setPastePricProv] = useState(''); // override manual del precio provincial
  const [pasteSaving, setPasteSaving] = useState(false);
  // URL editable: mismo patrón que idealista_url en PriceBasePage.
  const [pasteUrl, setPasteUrl] = useState('');
  const [pasteAutoUrl, setPasteAutoUrl] = useState('');
  const [provinces, setProvinces] = useState([]);
  // Lock por municipio (Enlace fijo) — mapa `${province_slug}::${muni_slug}::${type}` → row
  const [muniLocks, setMuniLocks] = useState({});
  const muniSlug = (name) => (name || '').split('/')[0].trim()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const loadLocks = async () => {
    // Cargamos locks para todas las provincias distintas presentes en `rows`.
    // Se hace en el load principal después de tener los rows.
    try {
      const slugs = Array.from(new Set(rows.map(r => r.province_slug))).filter(Boolean);
      const results = await Promise.allSettled(
        slugs.flatMap(slug => [
          axios.get(`${API}/market-prices/municipality-settings?province_slug=${slug}&price_type=SALE`, { withCredentials: true }),
          axios.get(`${API}/market-prices/municipality-settings?province_slug=${slug}&price_type=RENT`, { withCredentials: true }),
        ])
      );
      const acc = {};
      for (const r of results) {
        if (r.status !== 'fulfilled') continue;
        for (const it of (r.value.data.items || [])) {
          acc[`${it.province_slug}::${it.municipio_slug}::${it.price_type}`] = it;
        }
      }
      setMuniLocks(acc);
    } catch {}
  };

  const toggleLock = async (row, priceType) => {
    const slug = muniSlug(row.municipality);
    const key = `${row.province_slug}::${slug}::${priceType}`;
    const current = muniLocks[key];
    const nextLocked = !(current?.locked);
    try {
      await axios.put(`${API}/market-prices/municipality-lock`, {
        province_slug: row.province_slug,
        price_type: priceType,
        municipio_name: row.municipality,
        locked: nextLocked,
      }, { withCredentials: true });
      toast.success(nextLocked ? `${row.municipality} bloqueado (${priceType})` : `${row.municipality} desbloqueado (${priceType})`);
      loadLocks();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const unlockAllInProvince = async (province_slug, priceType) => {
    if (!window.confirm(`¿Desbloquear TODOS los municipios de ${province_slug} (${priceType})?`)) return;
    try {
      const res = await axios.post(`${API}/market-prices/unlock-all-in-province`,
        { province_slug, price_type: priceType }, { withCredentials: true });
      toast.success(`${res.data.unlocked} municipios desbloqueados`);
      loadLocks();
    } catch (err) { toast.error(errMsg(err)); }
  };

  useEffect(() => {
    axios.get(`${API}/market-prices/provinces`, { withCredentials: true })
      .then(r => setProvinces(r.data || [])).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [sumRes, listRes] = await Promise.all([
        axios.get(`${API}/market-prices/summary`, { withCredentials: true }),
        axios.get(`${API}/market-prices?active_only=true&limit=5000`, { withCredentials: true }),
      ]);
      setSummary(sumRes.data);
      // Agrupar por (municipio, barrio, provincia) con columnas SALE/RENT.
      const map = {};
      for (const r of listRes.data.items || []) {
        const barrio = r.neighborhood_name || '';
        const key = `${r.province_slug}::${r.municipality_name}::${barrio}`;
        if (!map[key]) map[key] = {
          municipality: r.municipality_name,
          neighborhood: barrio,
          province: r.province_name,
          province_slug: r.province_slug,
          sale: null, rent: null,
        };
        if (r.price_type === 'SALE') map[key].sale = r;
        else if (r.price_type === 'RENT') map[key].rent = r;
      }
      setRows(Object.values(map).sort((a, b) => {
        const ma = a.municipality.localeCompare(b.municipality);
        if (ma !== 0) return ma;
        return (a.neighborhood || '').localeCompare(b.neighborhood || '');
      }));
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (rows.length > 0) loadLocks(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [rows]);

  const runImport = async () => {
    if (!window.confirm('Lanzar importación de Fotocasa (todas las provincias)? Puede tardar varios minutos.')) return;
    setImporting(true);
    try {
      const res = await axios.post(`${API}/market-prices/import`, {}, { withCredentials: true, timeout: 600000 });
      const s = res.data || {};
      toast.success(`Importación completada — ${s.runs || 0} provincias, ${s.inserted || 0} nuevos, ${s.updated || 0} actualizados`);
      if (s.errors && s.errors.length) {
        toast.warning(`${s.errors.length} errores (ver consola para detalle)`);
        console.warn('Fotocasa import errors', s.errors);
      }
      await load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setImporting(false); }
  };

  const openHistory = async (municipality, price_type, province_slug) => {
    setHistoryOpen({ municipality, price_type });
    try {
      const res = await axios.get(`${API}/market-prices/history`, {
        params: { municipality, price_type, province_slug },
        withCredentials: true,
      });
      setHistory(res.data.items || []);
    } catch (err) { toast.error(errMsg(err)); setHistoryOpen(null); }
  };

  const openPaste = async (price_type) => {
    // Se abre con URL genérica de Alicante como plantilla; el usuario la edita
    // libremente y todos los metadatos (province_slug, price_type) se
    // autodetectan de la URL final antes de enviar. Mismo patrón que Idealista:
    // la URL es la identidad, no hace falta preseleccionar nada.
    setPasteType(price_type);
    const initial = provinces[0] || null;
    setPasteProvince(initial);
    setPasteText('');
    setPastePricProv('');
    if (initial) {
      await loadPasteUrl(initial.province_slug, price_type);
    } else {
      const prefix = price_type === 'RENT' ? 'alquiler/' : '';
      const auto = `https://www.fotocasa.es/es/indice-precio-vivienda/${prefix}alicante-alacant/todas-las-zonas`;
      setPasteUrl(auto);
      setPasteAutoUrl(auto);
    }
    setPasteOpen(true);
  };

  // Actualizar desde Fotocasa a partir de una fila concreta (mismo patrón que
  // el botón "Idealista" por fila en Base de Precios). Precarga la URL y abre
  // la página de Fotocasa en pestaña nueva. Un paste desde una página de
  // municipio recorre TODOS los distritos de una sola vez.
  const openPasteFromRow = async (row, price_type) => {
    const prefix = price_type === 'RENT' ? 'alquiler/' : '';
    const auto = `https://www.fotocasa.es/es/indice-precio-vivienda/${prefix}${row.province_slug}/todas-las-zonas`;
    let url = auto;
    try {
      const r = await axios.get(`${API}/market-prices/url`, {
        params: { province_slug: row.province_slug, price_type },
        withCredentials: true,
      });
      url = r.data.url || auto;
    } catch (err) { /* fallback a auto */ }
    // Abrimos la página de Fotocasa en nueva pestaña + preparamos el modal.
    window.open(url, '_blank', 'noopener,noreferrer');
    setPasteType(price_type);
    setPasteText('');
    setPastePricProv('');
    setPasteUrl(url);
    setPasteAutoUrl(auto);
    setPasteOpen(true);
  };

  // Extrae (slug, price_type, name) desde la URL editada por el usuario.
  // Soporta URLs de provincia y de municipio:
  //   /indice-precio-vivienda/<slug>/todas-las-zonas          → SALE
  //   /indice-precio-vivienda/alquiler/<slug>/todas-las-zonas → RENT
  // Cuando el slug NO coincide con una provincia conocida (p.ej. página de
  // municipio como `altea`), enviamos province_name="" y el backend resuelve
  // la provincia real desde el mapping oficial de price_m2_base. Nunca se
  // fabrica un nombre de provincia desde el slug.
  const parseFotocasaUrl = (url) => {
    if (!url) return null;
    const m = url.match(/indice-precio-vivienda\/(?:(alquiler)\/)?([^/]+)\/todas-las-zonas/i);
    if (!m) return null;
    const price_type = m[1] ? 'RENT' : 'SALE';
    const slug = m[2].toLowerCase();
    const match = (provinces || []).find(p => p.province_slug === slug);
    return {
      slug,
      price_type,
      // Sólo enviamos province_name cuando coincide con una provincia conocida;
      // en caso contrario se deja vacío para que el backend deduzca la real.
      province_name: match ? match.province_name : '',
      is_known_province: !!match,
    };
  };

  // Carga la URL de Fotocasa para (provincia, tipo). Mismo patrón que la URL
  // editable de Idealista en PriceBasePage.
  const loadPasteUrl = async (province_slug, price_type) => {
    try {
      const r = await axios.get(`${API}/market-prices/url`, {
        params: { province_slug, price_type },
        withCredentials: true,
      });
      setPasteUrl(r.data.url || '');
      setPasteAutoUrl(r.data.auto_url || '');
    } catch (err) {
      // Fallback: reconstruir la URL local si el endpoint falla.
      const prefix = price_type === 'RENT' ? 'alquiler/' : '';
      const auto = `https://www.fotocasa.es/es/indice-precio-vivienda/${prefix}${province_slug}/todas-las-zonas`;
      setPasteUrl(auto);
      setPasteAutoUrl(auto);
    }
  };

  const submitPaste = async () => {
    if ((pasteText || '').trim().length < 20) { toast.error('Pega el contenido de la página Fotocasa completo'); return; }
    // La URL es la fuente de verdad para province_slug y price_type.
    const info = parseFotocasaUrl(pasteUrl);
    if (!info) {
      toast.error('URL de Fotocasa no reconocida. Debe seguir el formato /indice-precio-vivienda/[alquiler/]<slug>/todas-las-zonas');
      return;
    }
    setPasteSaving(true);
    try {
      const payload = {
        province_name: info.province_name,
        province_slug: info.slug,
        price_type: info.price_type,
        raw_text: pasteText,
      };
      // Override manual del precio provincial (fuerza la limpieza aunque el
      // parser no lo detecte automáticamente).
      const provNum = Number((pastePricProv || '').toString().replace(',', '.'));
      if (pastePricProv && !Number.isNaN(provNum) && provNum > 0) {
        payload.provincial_price = provNum;
      }
      if (pasteUrl && pasteUrl !== pasteAutoUrl) {
        payload.fotocasa_url = pasteUrl;
      }
      const res = await axios.post(`${API}/market-prices/paste`, payload,
        { withCredentials: true, timeout: 60000 });
      const r = res.data || {};
      const parentInfo = r.parent_muni ? ` · Municipio: ${r.parent_muni}` : '';
      toast.success(`Importados ${r.inserted || 0} nuevos + ${r.updated || 0} actualizados (${r.valid || 0} válidos · ${r.fallback || 0} fallback · ${r.no_data || 0} sin dato municipal)${parentInfo}`);
      setPasteOpen(false);
      await load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setPasteSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-6" data-testid="settings-market-prices-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="heading-2 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-blue-600" /> FOTOCASA · Precios de Mercado</h2>
          <p className="body-text mt-1">Fuente única de precios municipales de venta y alquiler. Actualización mensual con histórico y media móvil de 3 meses.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => openPaste('SALE')}
            disabled={importing}
            className="btn-primary flex items-center gap-2 disabled:opacity-60"
            data-testid="market-prices-paste-sale-btn"
          >
            <Copy className="w-4 h-4" /> Pegar Venta
          </button>
          <button
            onClick={() => openPaste('RENT')}
            disabled={importing}
            className="btn-primary flex items-center gap-2 disabled:opacity-60"
            style={{ backgroundColor: '#f97316', borderColor: '#f97316' }}
            data-testid="market-prices-paste-rent-btn"
          >
            <Copy className="w-4 h-4" /> Pegar Alquiler
          </button>
          <button
            onClick={() => {
              const provOptions = Array.from(new Set(rows.map(r => r.province_slug))).filter(Boolean);
              if (!provOptions.length) { toast.info('No hay provincias cargadas'); return; }
              const p = window.prompt(`Desbloquear todos los municipios. Provincia (slug):\n${provOptions.join(', ')}`, provOptions[0]);
              if (!p) return;
              const t = window.prompt('Tipo (SALE o RENT):', 'SALE');
              if (t !== 'SALE' && t !== 'RENT') return;
              unlockAllInProvince(p, t);
            }}
            className="btn-secondary text-sm flex items-center gap-2"
            title="Desbloquea todos los locks de una provincia (salida de emergencia)"
            data-testid="market-prices-unlock-all-btn"
          >
            <Unlock className="w-4 h-4" /> Desbloquear todos
          </button>
          <button
            onClick={load}
            disabled={loading}
            className="btn-secondary text-sm flex items-center gap-2 disabled:opacity-60"
            data-testid="market-prices-refresh-btn"
            title="Recargar la tabla"
          >
            <RefreshCw className="w-4 h-4" /> Recargar
          </button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="card-base p-4">
            <p className="text-xs text-slate-400 uppercase">Venta · Válidos</p>
            <p className="text-2xl font-bold text-slate-800">{summary.by_type?.SALE?.VALID ?? 0}</p>
            <p className="text-xs text-amber-600 mt-1">{summary.by_type?.SALE?.POSSIBLE_PROVINCIAL_FALLBACK ?? 0} fallback · {summary.by_type?.SALE?.NO_MUNICIPAL_DATA ?? 0} sin dato</p>
          </div>
          <div className="card-base p-4">
            <p className="text-xs text-slate-400 uppercase">Alquiler · Válidos</p>
            <p className="text-2xl font-bold text-slate-800">{summary.by_type?.RENT?.VALID ?? 0}</p>
            <p className="text-xs text-amber-600 mt-1">{summary.by_type?.RENT?.POSSIBLE_PROVINCIAL_FALLBACK ?? 0} fallback · {summary.by_type?.RENT?.NO_MUNICIPAL_DATA ?? 0} sin dato</p>
          </div>
          <div className="card-base p-4 col-span-2">
            <p className="text-xs text-slate-400 uppercase">Última importación</p>
            <p className="text-sm font-medium text-slate-800 mt-1">
              {summary.last_import
                ? `${summary.last_import.reference_date} — ${new Date(summary.last_import.imported_at).toLocaleString('es-ES')}`
                : 'Sin importaciones previas'}
            </p>
          </div>
        </div>
      )}

      <div className="card-base p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="px-4 py-3 text-left">Provincia</th>
                <th className="px-4 py-3 text-left">Municipio</th>
                <th className="px-4 py-3 text-center" title="Enlace fijo: no se sobreescribe en imports provinciales">Lock</th>
                <th className="px-4 py-3 text-left">Barrio</th>
                <th className="px-4 py-3 text-right">€/m² Venta</th>
                <th className="px-4 py-3 text-right">€/m² Alquiler</th>
                <th className="px-4 py-3 text-center">Fecha</th>
                <th className="px-4 py-3 text-center">Estado Venta</th>
                <th className="px-4 py-3 text-center">Estado Alquiler</th>
                <th className="px-4 py-3 text-center">Histórico</th>
                <th className="px-4 py-3 text-center">Acción</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 && (
                <tr><td colSpan={11} className="text-center text-slate-400 py-8">
                  {'Sin datos aún. Usa "Pegar Venta" o "Pegar Alquiler" para importar precios de Fotocasa.'}
                </td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.province_slug + '::' + r.municipality + '::' + (r.neighborhood || '')} className="hover:bg-slate-50">
                  <td className="px-4 py-2 text-slate-500">{r.province}</td>
                  <td className="px-4 py-2 font-medium text-slate-800">{r.municipality}</td>
                  <td className="px-4 py-2 text-center">
                    {(() => {
                      const slug = muniSlug(r.municipality);
                      const saleLock = muniLocks[`${r.province_slug}::${slug}::SALE`];
                      const rentLock = muniLocks[`${r.province_slug}::${slug}::RENT`];
                      const anyLocked = saleLock?.locked || rentLock?.locked;
                      const badge = (type, lock) => {
                        const isLocked = lock?.locked;
                        const reason = lock?.locked_by === 'barrios_importados' ? 'auto (barrios)' : 'manual';
                        return (
                          <button
                            key={type}
                            type="button"
                            onClick={() => toggleLock(r, type)}
                            title={isLocked ? `Enlace fijo ${type} · ${reason} · click para desbloquear` : `Sin lock ${type} · click para bloquear`}
                            className={`inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded border transition ${
                              isLocked
                                ? 'bg-amber-100 text-amber-800 border-amber-300 font-semibold'
                                : 'bg-slate-50 text-slate-400 border-slate-200 hover:bg-slate-100'
                            }`}
                            data-testid={`market-lock-${type}-${r.province_slug}-${slug}`}
                          >
                            {isLocked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
                            <span>{type[0]}</span>
                          </button>
                        );
                      };
                      return (
                        <div className="flex gap-1 justify-center">
                          {badge('SALE', saleLock)}
                          {badge('RENT', rentLock)}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-2 text-slate-600">
                    {r.neighborhood || <span className="text-slate-300">–</span>}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {r.sale ? (r.sale.moving_average_3m ?? r.sale.price_m2).toLocaleString('es-ES') + ' €' : <span className="text-slate-300">–</span>}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums" style={{ color: r.rent ? '#f97316' : undefined }}>
                    {r.rent ? (r.rent.moving_average_3m ?? r.rent.price_m2).toLocaleString('es-ES') + ' €' : <span className="text-slate-300">–</span>}
                  </td>
                  <td className="px-4 py-2 text-center text-xs text-slate-500">{r.sale?.reference_date || r.rent?.reference_date || '–'}</td>
                  <td className="px-4 py-2 text-center"><StatusBadge status={r.sale?.status || 'SIN_DATO'} /></td>
                  <td className="px-4 py-2 text-center"><StatusBadge status={r.rent?.status || 'SIN_DATO'} /></td>
                  <td className="px-4 py-2 text-center">
                    <button
                      className="text-xs text-blue-600 hover:underline"
                      onClick={() => openHistory(r.municipality, r.sale ? 'SALE' : 'RENT', r.province_slug)}
                      data-testid={`market-history-${r.province_slug}-${r.municipality}`}
                    >
                      Ver histórico
                    </button>
                  </td>
                  <td className="px-4 py-2 text-center">
                    {/* Botón "Fotocasa" por fila — mismo patrón que "Idealista"
                        en la pestaña Base de Precios. Abre la página de
                        Fotocasa en nueva pestaña y el modal para pegar.
                        Un único paste desde página de municipio actualiza
                        todos los distritos de esa fila y sus hermanos. */}
                    <div className="flex items-center gap-1 justify-center">
                      <button
                        type="button"
                        onClick={() => openPasteFromRow(r, 'SALE')}
                        className="text-[11px] px-2 py-1 rounded border border-blue-200 text-blue-700 hover:bg-blue-50"
                        title={`Actualizar VENTA desde Fotocasa · ${r.municipality}${r.neighborhood ? ' · ' + r.neighborhood : ''}`}
                        data-testid={`market-refresh-sale-${r.province_slug}-${r.municipality}`}
                      >
                        Fotocasa V
                      </button>
                      <button
                        type="button"
                        onClick={() => openPasteFromRow(r, 'RENT')}
                        className="text-[11px] px-2 py-1 rounded border border-orange-200 text-orange-600 hover:bg-orange-50"
                        title={`Actualizar ALQUILER desde Fotocasa · ${r.municipality}${r.neighborhood ? ' · ' + r.neighborhood : ''}`}
                        data-testid={`market-refresh-rent-${r.province_slug}-${r.municipality}`}
                      >
                        Fotocasa A
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>


      {/* Modal de importación manual (pegado de Fotocasa) */}
      {pasteOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => !pasteSaving && setPasteOpen(false)}>
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">
                Importación manual · {pasteType === 'SALE' ? 'Venta' : 'Alquiler'}
              </h3>
              <button className="text-slate-400 hover:text-slate-600" onClick={() => setPasteOpen(false)} disabled={pasteSaving}>×</button>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto">
              {/* URL editable como fuente única de verdad — mismo patrón que
                  Idealista. El slug de provincia y el price_type se extraen
                  automáticamente al procesar. Vale tanto una URL de provincia
                  como de municipio (Fotocasa las llama distritos). */}
              <div>
                <label className="text-xs font-medium text-slate-600 flex items-center gap-2">
                  URL de Fotocasa
                  {(() => {
                    const info = parseFotocasaUrl(pasteUrl);
                    if (!info) return <span className="text-[10px] text-red-500">(URL no reconocida)</span>;
                    const provLabel = info.is_known_province
                      ? <b>{info.province_name}</b>
                      : <>slug <b>{info.slug}</b> (provincia autodetectada al procesar)</>;
                    return (
                      <span className="text-[10px] font-normal text-slate-500">
                        Detectado: {provLabel} · <b>{info.price_type === 'RENT' ? 'Alquiler' : 'Venta'}</b>
                      </span>
                    );
                  })()}
                </label>
                <div className="flex gap-2 mt-1">
                  <input
                    type="text"
                    value={pasteUrl}
                    onChange={(e) => setPasteUrl(e.target.value)}
                    className="input-base flex-1 text-xs font-mono"
                    placeholder="https://www.fotocasa.es/es/indice-precio-vivienda/[alquiler/]<slug>/todas-las-zonas"
                    data-testid="paste-fotocasa-url-input"
                  />
                  <button
                    type="button"
                    onClick={() => pasteUrl && window.open(pasteUrl, '_blank', 'noopener,noreferrer')}
                    className="btn-secondary text-xs whitespace-nowrap"
                    title="Abrir esta URL en nueva pestaña"
                    data-testid="paste-fotocasa-url-open"
                  >
                    Abrir
                  </button>
                </div>
                <p className="text-[11px] mt-1 text-slate-500">
                  Puedes pegar la URL de una provincia (lista municipios) o de un municipio (lista distritos/barrios). Detectamos automáticamente el tipo y el slug.
                </p>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">
                  Precio provincial (opcional — fuerza la limpieza si el parser no lo detecta)
                </label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="number"
                    step="0.01"
                    value={pastePricProv}
                    onChange={(e) => setPastePricProv(e.target.value)}
                    className="input-base w-40 text-sm text-right tabular-nums"
                    placeholder="Ej. 3173"
                    data-testid="paste-provincial-price-input"
                  />
                  <span className="text-xs text-slate-500">€/m²</span>
                  <span className="text-xs text-slate-400">
                    Todos los municipios con este precio exacto se marcarán como &quot;sin dato municipal&quot; y no se usarán en cálculos.
                  </span>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Contenido pegado</label>
                <textarea
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  className="input-base w-full text-xs font-mono mt-1"
                  rows={16}
                  placeholder="Pega aquí la tabla completa de municipios y precios de Fotocasa..."
                  data-testid="paste-textarea"
                />
                <p className="text-xs text-slate-400 mt-1">
                  {(pasteText || '').length} caracteres. Se detectan filas con {'"nombre  precio €/m²"'}.
                </p>
              </div>
            </div>
            <div className="p-4 border-t flex items-center justify-end gap-2">
              <button className="btn-secondary text-sm" onClick={() => setPasteOpen(false)} disabled={pasteSaving} data-testid="paste-cancel">
                Cancelar
              </button>
              <button
                className="btn-primary text-sm flex items-center gap-2 disabled:opacity-60"
                onClick={submitPaste}
                disabled={pasteSaving}
                data-testid="paste-submit"
              >
                {pasteSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {pasteSaving ? 'Guardando...' : 'Importar'}
              </button>
            </div>
          </div>
        </div>
      )}


      {historyOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setHistoryOpen(null)}>
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold">Histórico — {historyOpen.municipality} · {historyOpen.price_type === 'SALE' ? 'Venta' : 'Alquiler'}</h3>
              <button className="text-slate-400 hover:text-slate-600" onClick={() => setHistoryOpen(null)}>×</button>
            </div>
            <div className="p-4 max-h-[60vh] overflow-y-auto">
              {history.length === 0 ? (
                <p className="text-slate-400 text-sm">Sin histórico</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-slate-500">
                    <tr>
                      <th className="py-2 text-left">Mes</th>
                      <th className="py-2 text-right">€/m²</th>
                      <th className="py-2 text-right">MA 3m</th>
                      <th className="py-2 text-center">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {history.map((h) => (
                      <tr key={h.reference_date + h.imported_at}>
                        <td className="py-2">{h.reference_date}</td>
                        <td className="py-2 text-right tabular-nums">{Number(h.price_m2).toLocaleString('es-ES')} €</td>
                        <td className="py-2 text-right tabular-nums text-slate-500">{h.moving_average_3m != null ? Number(h.moving_average_3m).toLocaleString('es-ES') + ' €' : '–'}</td>
                        <td className="py-2 text-center"><StatusBadge status={h.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

